import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { memo } from "react";
import { useUsersContext } from "../context";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module, NO_DATA, PATH_CONTRACTOR } from "@/constants";
import UserUpdateForm from "./UserUpdateForm";
import UserBadge from "./UserBadge";
import { DetailsButton } from "@/components/DetailsButton";

const UserDetails = memo(() => {
  const { t } = useTranslation();
  const { data: user } = useUsersContext();
  const { can_update } = useModulePermissions(Module.Users);
  const contractorsPermissions = useModulePermissions(Module.Contractors);

  if (!user) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("users.details.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.ref_number")}
            </dt>
            <dd className="text-sm text-gray-900">{user.ref_number}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.first_name")}
            </dt>
            <dd className="text-sm text-gray-900">{user.first_name}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.lastName")}
            </dt>
            <dd className="text-sm text-gray-900">{user.last_name}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.email")}
            </dt>
            <dd className="text-sm text-gray-900">{user.email}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.phoneNumber")}
            </dt>
            <dd className="text-sm text-gray-900">{user.phone_number}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.systemRole")}
            </dt>
            <dd className="text-sm text-gray-900">{user.system_role_name}</dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.isActive")}
            </dt>
            <dd className="text-sm text-gray-900">
              <Badge variant={user.is_active ? "secondary" : "destructive"}>
                {user.is_active
                  ? t("users.status.active")
                  : t("users.status.inactive")}
              </Badge>
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.preferredLanguage")}
            </dt>
            <dd className="text-sm text-gray-900">
              {t(`language.${user.preferred_language_iso_code}.name`, NO_DATA)}
            </dd>
          </div>
          <div className="space-y-1">
            <dt className="text-sm font-medium text-gray-500">
              {t("users.fields.cognitoId")}
            </dt>
            <dd className="text-sm text-gray-900 font-mono">
              {user.cognito_id}
            </dd>
          </div>
          {user.contractor_id && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("users.fields.contractor_is_active")}
              </dt>
              <dd className="text-sm text-gray-900">
                <UserBadge isActive={user.contractor_is_active} />
              </dd>
            </div>
          )}
          {user.contractor_id && contractorsPermissions.can_read && (
            <div className="space-y-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("users.details.contractorCard")}
              </dt>
              <dd className="text-sm text-gray-900">
                <DetailsButton
                  path={PATH_CONTRACTOR}
                  pathParams={{ contractorId: user.contractor_id }}
                  label="users.details.contractorDetailsButton"
                />
              </dd>
            </div>
          )}
        </dl>

        {can_update && (
          <>
            <Separator className="my-6" />
            <UserUpdateForm />
          </>
        )}
      </CardContent>
    </Card>
  );
});

export default UserDetails;
